import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import MyProLogo from './MyProLogo';

const spin = keyframes`to { transform: rotate(360deg); }`;
const bob = keyframes`
  0%, 100% { transform: translateY(0) rotate(-3deg); }
  50% { transform: translateY(-9px) rotate(3deg); }
`;
const shadow = keyframes`
  0%, 100% { transform: scaleX(1); opacity: .28; }
  50% { transform: scaleX(.65); opacity: .12; }
`;
const build = keyframes`
  0%, 100% { transform: scaleY(.35); }
  50% { transform: scaleY(1); }
`;
const dots = keyframes`
  0% { content: ''; }
  25% { content: '.'; }
  50% { content: '..'; }
  75% { content: '...'; }
`;

// Gedung mini: [lebar, tinggi, warna]
const BUILDINGS = [
  [10, 22, 'var(--mp-accent)'],
  [12, 34, 'var(--mp-brand)'],
  [10, 26, '#F59E0B'],
  [12, 38, 'var(--mp-brand)'],
  [10, 20, 'var(--mp-accent)'],
];

/**
 * Indikator memuat bertema properti: logo MYPRO melayang di dalam cincin
 * gradasi yang berputar, dengan barisan gedung mini yang naik-turun di bawahnya.
 * size="sm" untuk area sempit (hanya logo + cincin, tanpa gedung/teks).
 */
export default function MyproLoader({ label = 'Memuat data', size = 'md', fullHeight = false, sx }) {
  const small = size === 'sm';
  const tile = small ? 44 : 76;
  const ring = tile + (small ? 14 : 22);
  const logo = small ? 28 : 46;

  return (
    <Box
      role="status"
      aria-label={label}
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: small ? 0 : 2,
        py: small ? 1 : 4, width: '100%', ...(fullHeight ? { minHeight: '60vh' } : {}), ...sx,
      }}
    >
      <Box sx={{ position: 'relative', width: ring, height: ring, display: 'grid', placeItems: 'center' }}>
        {/* cincin gradasi berputar */}
        <Box
          sx={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'conic-gradient(from 0deg, rgba(var(--mp-brand-rgb), 0) 0deg, var(--mp-accent) 200deg, #F59E0B 330deg, rgba(245,158,11,0) 360deg)',
            WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${small ? 3 : 4}px), #000 calc(100% - ${small ? 3 : 4}px))`,
            mask: `radial-gradient(farthest-side, transparent calc(100% - ${small ? 3 : 4}px), #000 calc(100% - ${small ? 3 : 4}px))`,
            animation: `${spin} 1.1s linear infinite`,
          }}
        />
        {/* logo melayang */}
        <Box
          sx={{
            width: tile, height: tile, borderRadius: small ? '14px' : '24px', bgcolor: '#fff', display: 'grid', placeItems: 'center',
            boxShadow: '0 14px 28px -12px rgba(var(--mp-brand-rgb), 0.55), 0 0 0 3px rgba(255,201,77,0.4)',
            animation: `${bob} 1.6s ease-in-out infinite`,
          }}
        >
          <MyProLogo width={`${logo}px`} height={`${logo}px`} />
        </Box>
      </Box>

      {!small && (
        <>
          <Box sx={{ width: 54, height: 7, borderRadius: '50%', bgcolor: 'var(--mp-brand)', animation: `${shadow} 1.6s ease-in-out infinite`, mt: -1.5 }} />
          {/* barisan gedung mini */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '5px', height: 40 }}>
            {BUILDINGS.map(([w, h, c], i) => (
              <Box
                key={i}
                sx={{
                  width: w, height: h, bgcolor: c, borderRadius: '3px 3px 0 0', transformOrigin: 'bottom',
                  animation: `${build} 1.3s ease-in-out ${i * 0.13}s infinite`,
                  backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.0) 0 5px, rgba(255,255,255,0.35) 5px 7px)',
                }}
              />
            ))}
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: 'var(--mp-brand)', letterSpacing: '0.02em' }}>
            {label}
            <Box component="span" sx={{ display: 'inline-block', width: 18, textAlign: 'left', '&::after': { content: '""', animation: `${dots} 1.4s steps(1) infinite` } }} />
          </Typography>
        </>
      )}
    </Box>
  );
}
